import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Truck, 
  MapPin, 
  Calendar, 
  Package, 
  Briefcase,
  Phone,
  Mail,
  ArrowRight,
  Euro,
  Weight,
  RefreshCw,
  ArrowLeft,
  Info
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";

interface CarrierListing {
  id: string;
  carrier_name: string;
  carrier_phone: string;
  carrier_email: string | null;
  departure_city: string;
  arrival_city: string;
  stopover_cities: string | null;
  departure_date: string;
  frequency: string;
  transport_colis: boolean;
  transport_bagages: boolean;
  transport_autres: boolean;
  transport_autres_details: string | null;
  price_per_kg: number;
  max_weight_kg: number | null;
  conditions: string | null;
  created_at: string;
  status: string;
}

const AnnonceDetail = () => {
  const { id } = useParams<{ id: string }>();

  const { data: listing, isLoading, error } = useQuery({
    queryKey: ["carrier-listing", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("carrier_listings")
        .select("*")
        .eq("id", id)
        .eq("status", "approved")
        .single();

      if (error) throw error;
      return data as CarrierListing;
    },
    enabled: !!id,
  });

  const getFrequencyLabel = (frequency: string) => {
    const labels: Record<string, string> = {
      ponctuel: "Ponctuel",
      hebdomadaire: "Hebdomadaire",
      mensuel: "Mensuel",
      regulier: "Régulier",
    };
    return labels[frequency] || frequency;
  };

  const getTransportTypes = (listing: CarrierListing) => {
    const types: string[] = [];
    if (listing.transport_colis) types.push("Colis");
    if (listing.transport_bagages) types.push("Bagages");
    if (listing.transport_autres) types.push(listing.transport_autres_details || "Autres");
    return types;
  };

  return (
    <>
      <Helmet>
        <title>
          {listing 
            ? `${listing.carrier_name} - ${listing.departure_city} → ${listing.arrival_city} | GoBledPro`
            : "Détail de l'annonce | GoBledPro"
          }
        </title>
        <meta
          name="description"
          content={listing 
            ? `Transport ${listing.departure_city} vers ${listing.arrival_city} par ${listing.carrier_name}. ${listing.price_per_kg}€/kg.`
            : "Consultez les détails de cette annonce de transport France-Maroc."
          }
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-1 py-8 md:py-12">
          <div className="container mx-auto px-4">
            {/* Back Button */}
            <div className="mb-6">
              <Button variant="ghost" asChild className="gap-2">
                <Link to="/annonces-transporteurs">
                  <ArrowLeft className="w-4 h-4" />
                  Retour aux annonces
                </Link>
              </Button>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="max-w-2xl mx-auto">
                <Card>
                  <CardContent className="p-8 space-y-6">
                    <Skeleton className="h-8 w-3/4" />
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <div className="bg-destructive/10 text-destructive p-6 rounded-lg max-w-md mx-auto">
                  <p className="font-medium">Annonce introuvable</p>
                  <p className="text-sm mt-2 opacity-80">
                    Cette annonce n'existe pas ou n'est plus disponible.
                  </p>
                  <Button asChild className="mt-4">
                    <Link to="/annonces-transporteurs">Voir toutes les annonces</Link>
                  </Button>
                </div>
              </div>
            )}

            {/* Listing Detail */}
            {!isLoading && !error && listing && (
              <div className="max-w-2xl mx-auto">
                <Card className="overflow-hidden border-2">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                          <Truck className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                            {listing.carrier_name}
                          </h1>
                          <Badge variant="secondary" className="mt-1">
                            <RefreshCw className="w-3 h-3 mr-1" />
                            {getFrequencyLabel(listing.frequency)}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Route */}
                    <div className="flex items-center gap-3 text-lg mt-4">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="font-bold">{listing.departure_city}</span>
                      <ArrowRight className="w-5 h-5 text-muted-foreground" />
                      <span className="font-bold">{listing.arrival_city}</span>
                    </div>
                    
                    {listing.stopover_cities && (
                      <p className="text-sm text-muted-foreground mt-2 pl-8">
                        via {listing.stopover_cities}
                      </p>
                    )}
                  </div>

                  <CardContent className="p-6 md:p-8 space-y-6">
                    {/* Date */}
                    <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                      <Calendar className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Date de départ</p>
                        <p className="font-semibold text-lg">
                          {format(new Date(listing.departure_date), "EEEE d MMMM yyyy", { locale: fr })}
                        </p>
                      </div>
                    </div>

                    {/* Transport Types */}
                    <div>
                      <p className="text-sm text-muted-foreground mb-3">Types de transport acceptés</p>
                      <div className="flex flex-wrap gap-2">
                        {getTransportTypes(listing).map((type, index) => (
                          <Badge key={index} variant="outline" className="px-4 py-2 text-sm">
                            {type === "Colis" && <Package className="w-4 h-4 mr-2" />}
                            {type === "Bagages" && <Briefcase className="w-4 h-4 mr-2" />}
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Price & Weight */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-primary/10 p-4 rounded-lg text-center">
                        <Euro className="w-6 h-6 text-primary mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Prix</p>
                        <p className="font-bold text-2xl text-primary">{listing.price_per_kg}€/kg</p>
                      </div>
                      {listing.max_weight_kg && (
                        <div className="bg-muted/50 p-4 rounded-lg text-center">
                          <Weight className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Poids max</p>
                          <p className="font-bold text-2xl">{listing.max_weight_kg} kg</p>
                        </div>
                      )}
                    </div>

                    {/* Conditions */}
                    {listing.conditions && (
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Info className="w-4 h-4 text-muted-foreground" />
                          <p className="text-sm font-medium">Conditions</p>
                        </div>
                        <p className="text-sm text-muted-foreground">{listing.conditions}</p>
                      </div>
                    )}

                    {/* Contact Section */}
                    <div className="border-t pt-6 space-y-3">
                      <p className="font-semibold text-center mb-4">Contacter le transporteur</p>
                      
                      <a href={`tel:${listing.carrier_phone}`} className="block">
                        <Button className="w-full" size="lg">
                          <Phone className="w-5 h-5 mr-2" />
                          Appeler : {listing.carrier_phone}
                        </Button>
                      </a>
                      
                      {listing.carrier_email && (
                        <a href={`mailto:${listing.carrier_email}`} className="block">
                          <Button variant="outline" className="w-full" size="lg">
                            <Mail className="w-5 h-5 mr-2" />
                            {listing.carrier_email}
                          </Button>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Info Banner */}
                <div className="mt-8 bg-muted/50 border rounded-xl p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    <strong>GoBledPro</strong> facilite la mise en relation. Le transport est assuré par des professionnels indépendants.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AnnonceDetail;
